import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, ShieldCheck, Radar as RadarIcon } from "lucide-react";

const EVENTS = [
  { sev: "high", tag: "T1078", src: "Identity", text: "Impossible-travel sign-in blocked", state: "Contained" },
  { sev: "med", tag: "T1059.001", src: "Endpoint", text: "Encoded PowerShell execution flagged", state: "Triaged" },
  { sev: "high", tag: "T1486", src: "Server", text: "Mass file-rename pattern isolated", state: "Contained" },
  { sev: "low", tag: "T1526", src: "Cloud", text: "New IAM role enumerated by user", state: "Reviewed" },
  { sev: "med", tag: "T1566", src: "Email", text: "Credential-harvest link detonated", state: "Blocked" },
  { sev: "low", tag: "T1046", src: "Network", text: "Internal port sweep correlated", state: "Reviewed" },
  { sev: "high", tag: "T1098", src: "Identity", text: "MFA method added then removed", state: "Escalated" },
];

const SEV = {
  high: { dot: "bg-[#F2707A]", ring: "border-[#F2707A]/40", label: "text-[#F2707A]" },
  med: { dot: "bg-[#E7C46A]", ring: "border-[#E7C46A]/40", label: "text-[#E7C46A]" },
  low: { dot: "bg-[#8E9AEA]", ring: "border-[#8E9AEA]/40", label: "text-[#8E9AEA]" },
};

const PIPELINE = ["Collect", "Correlate", "Triage", "Contain"];

const Blip = ({ cx, cy, delay }) => (
  <motion.circle
    cx={cx}
    cy={cy}
    r="2.6"
    fill="#8E9AEA"
    initial={{ opacity: 0, r: 2.6 }}
    animate={{ opacity: [0, 1, 1, 0], r: [2.6, 3.6, 2.6] }}
    transition={{ duration: 4, repeat: Infinity, delay, ease: "easeInOut" }}
  />
);

const Radar = () => (
  <svg viewBox="0 0 200 200" className="h-full w-full" aria-hidden data-testid="soc-radar">
    <defs>
      <linearGradient id="sweep" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#8E9AEA" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#8E9AEA" stopOpacity="0" />
      </linearGradient>
    </defs>
    {[34, 58, 82].map((r) => (
      <circle key={r} cx="100" cy="100" r={r} fill="none" stroke="#8E9AEA" strokeOpacity="0.16" />
    ))}
    <line x1="18" y1="100" x2="182" y2="100" stroke="#8E9AEA" strokeOpacity="0.12" />
    <line x1="100" y1="18" x2="100" y2="182" stroke="#8E9AEA" strokeOpacity="0.12" />
    <g className="soc-radar-sweep">
      <path d="M100 100 L182 100 A82 82 0 0 0 158 42 Z" fill="url(#sweep)" />
      <line x1="100" y1="100" x2="182" y2="100" stroke="#8E9AEA" strokeOpacity="0.6" />
    </g>
    <Blip cx="62" cy="74" delay={0.2} />
    <Blip cx="138" cy="66" delay={1.4} />
    <Blip cx="126" cy="140" delay={2.6} />
    <Blip cx="74" cy="132" delay={3.4} />
    <motion.circle
      cx="100"
      cy="100"
      r="6"
      fill="none"
      stroke="#8E9AEA"
      strokeOpacity="0.55"
      initial={{ r: 6, opacity: 0.55 }}
      animate={{ r: [6, 26], opacity: [0.55, 0] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
    />
    <circle cx="100" cy="100" r="5" fill="#8E9AEA" />
  </svg>
);

export const SocConsole = () => {
  const [head, setHead] = useState(0);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const feed = setInterval(() => setHead((h) => (h + 1) % EVENTS.length), 2800);
    const pipe = setInterval(() => setStep((s) => (s + 1) % PIPELINE.length), 1600);
    return () => {
      clearInterval(feed);
      clearInterval(pipe);
    };
  }, []);

  const rows = [0, 1, 2].map((i) => EVENTS[(head + i) % EVENTS.length]);

  return (
    <div
      className="glass-card relative overflow-hidden p-3.5 sm:p-5"
      data-testid="soc-console"
    >
      <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#7BE0B0] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#7BE0B0]" />
          </span>
          <span className="truncate text-[10px] font-semibold uppercase tracking-[0.2em] text-[#DDE3F2] sm:text-[11px]">
            Threat Operations
          </span>
        </div>
        <span className="shrink-0 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[9px] font-medium uppercase tracking-[0.14em] text-[#A6B0C9] sm:text-[10px]">
          Illustrative view
        </span>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_1.35fr] sm:items-center">
        <div className="mx-auto h-32 w-32 sm:h-auto sm:w-full sm:max-w-[190px]">
          <Radar />
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A6B0C9]">
            <Activity className="h-3.5 w-3.5 text-[#8E9AEA]" />
            Correlated signals
          </div>
          <div className="mt-2.5 space-y-2" data-testid="soc-console-feed">
            <AnimatePresence initial={false} mode="popLayout">
              {rows.map((e, i) => (
                <motion.div
                  key={`${e.tag}-${head}-${i}`}
                  layout
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: i === 0 ? 1 : 0.62, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className={`flex items-start gap-2.5 rounded-lg border bg-white/[0.03] px-2.5 py-2 ${SEV[e.sev].ring}`}
                >
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${SEV[e.sev].dot}`} />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-[12px] font-medium leading-snug text-white sm:text-[13px]">{e.text}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] uppercase tracking-[0.12em] text-[#A6B0C9]">
                      <span>{e.src}</span>
                      <span className="text-[#8E9AEA]">{e.tag}</span>
                      <span className={SEV[e.sev].label}>{e.state}</span>
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="mt-4 border-t border-white/10 pt-3">
        <div className="flex items-center gap-1.5 sm:gap-2" data-testid="soc-console-pipeline">
          {PIPELINE.map((p, i) => (
            <React.Fragment key={p}>
              <div
                className={`flex-1 rounded-md border px-1.5 py-1.5 text-center text-[9px] font-semibold uppercase tracking-[0.1em] transition-colors duration-500 sm:text-[10px] ${
                  i <= step
                    ? "border-[#8E9AEA]/50 bg-[#8E9AEA]/15 text-white"
                    : "border-white/10 bg-white/[0.02] text-[#A6B0C9]"
                }`}
              >
                {p}
              </div>
              {i < PIPELINE.length - 1 && (
                <span className={`h-px w-2 shrink-0 sm:w-3 ${i < step ? "bg-[#8E9AEA]/60" : "bg-white/10"}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <p className="mt-3 flex items-start gap-2 text-[10px] leading-relaxed text-[#A6B0C9] sm:text-[11px]">
          <ShieldCheck className="mt-px h-3.5 w-3.5 shrink-0 text-[#8E9AEA]" />
          Human analyst review on every escalation — no alert closed by automation alone.
        </p>
      </div>

      <RadarIcon className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 text-white/[0.03]" aria-hidden />
    </div>
  );
};
